/**
 * Color tokens — single source of truth for every color in the app.
 *
 * Mirrors the "Color Tokens" frame in Figma (BeforeYouBuild file,
 * transitions.dev collection). Each token has a Light and Dark value.
 * Figma path: https://www.figma.com/design/tOkxmMumUQOCkfNSoThZLx/BeforeYouBuild?node-id=477-170
 *
 * NOTHING in the UI consumes these yet. This file is intentionally the
 * staging ground for a tactical, screen-by-screen color migration in a
 * later session. To start consuming them:
 *   - In CSS: inject `colorTokensCss` once at the app root, then reference
 *     `var(--color-<group>-<name>)` (e.g. `var(--color-text-default)`).
 *   - In TS/Three.js: read `colorTokens.<group>.<name>.light` / `.dark`,
 *     or use `resolveColor(token, mode)`.
 *
 * Keep this the ONLY place hex values live. Generate the CSS from the data
 * (via `colorTokensCss`) so the .ts and the stylesheet can never drift.
 */

export type ThemeMode = "light" | "dark";

export interface ColorToken {
  /** Figma token path, e.g. "fill/bg". Stable identity for the token. */
  readonly path: string;
  /** Group bucket, e.g. "fill". */
  readonly group: string;
  /** Leaf name within the group, e.g. "bg". */
  readonly name: string;
  /** CSS custom property name, e.g. "--color-fill-bg". */
  readonly cssVar: string;
  /** Light-mode CSS color value. */
  readonly light: string;
  /** Dark-mode CSS color value. */
  readonly dark: string;
  /** Optional human note (e.g. opacity caveats from the Figma source). */
  readonly note?: string;
}

type TokenSeed = {
  light: string;
  dark: string;
  note?: string;
};

type GroupSeed = Record<string, TokenSeed>;

/**
 * Raw token values, grouped exactly as in Figma. This is the editable
 * source. Everything else in this file is derived from it.
 */
const SEED = {
  fill: {
    bg: { light: "#fafafa", dark: "#080808" },
    pure: { light: "#ffffff", dark: "#000000" },
    "bg-inverse": { light: "#000000", dark: "#ffffff" },
    "row-hover": {
      light: "rgba(0, 0, 0, 0.05)",
      dark: "rgba(255, 255, 255, 0.05)",
      note: "Light is #000000 at 5% opacity; dark is #FFFFFF at 5% opacity."
    }
  },
  text: {
    default: { light: "#0d0d0d", dark: "#ededed" },
    subtle: { light: "#666666", dark: "#b2b2b2" },
    faint: { light: "#999999", dark: "#666666" },
    white: { light: "#ededed", dark: "#ededed" },
    black: { light: "#0d0d0d", dark: "#0d0d0d" },
    inverse: { light: "#ededed", dark: "#0d0d0d" }
  },
  misc: {
    muted: { light: "#5e6073", dark: "#767676" },
    "label-mono": { light: "#5e6073", dark: "#5e6073" },
    skeleton: {
      light: "#eeeeef",
      dark: "rgba(238, 238, 239, 0.1)",
      note: "Dark is #EEEEEF at 10% opacity."
    }
  },
  icon: {
    default: { light: "#0d0d0d", dark: "#ededed" },
    muted: { light: "#b2b2b2", dark: "#666666" },
    inverse: { light: "#ededed", dark: "#0d0d0d" }
  },
  card: {
    bg: { light: "#ffffff", dark: "#080808" },
    border: { light: "#f2f2f2", dark: "#1a1a1a" },
    "elevation-1": { light: "#f2f2f2", dark: "#0d0d0d" }
  },
  button: {
    bg: { light: "#f0f0f0", dark: "#141414" },
    "bg-hover": { light: "#e5e5e5", dark: "#303030" },
    "bg-brand": { light: "#6b715f", dark: "#6b715f" },
    "bg-brand-hover": { light: "#7d856e", dark: "#7d856e" },
    stroke: { light: "#e5e5e5", dark: "#1a1a1a" }
  },
  brand: {
    team: { light: "#4e6cc2", dark: "#4e6cc2" },
    pro: { light: "#d7cf9c", dark: "#d7cf9c" },
    lotscan: { light: "#6b715f", dark: "#6b715f" }
  },
  field: {
    default: { light: "#ffffff", dark: "#000000" },
    "border-default": { light: "#e5e5e5", dark: "#1a1a1a" },
    "border-selected": { light: "#6b715f", dark: "#6b715f" },
    selected: { light: "#ffffff", dark: "#000000" }
  },
  alert: {
    critical: { light: "#c24e4e", dark: "#c24e4e" },
    severe: {
      light: "#c2704e",
      dark: "#c2704e",
      note: "Between warning and critical — footprint/slope severity scale."
    },
    warning: { light: "#c2964e", dark: "#c2964e" },
    success: { light: "#65a765", dark: "#65a765" }
  },
  divider: {
    light: { light: "#e5e5e5", dark: "#1a1a1a" }
  },
  border: {
    dark: { light: "#FAFAFA", dark: "#000000" }
  },
  // Sculpted cartography: parchment/olive by day, neutral graphite at night.
  // Orange belongs to the parcel, while analysis colors retain their meaning.
  // Scene-only values; the surrounding application's token groups stay fixed.
  scene: {
    // ── Natural-environment palette ──────────────────────────────────────
    // The scene reads as land, not as a render: mossy/olive terrain lit by a
    // warm sun, earth-brown contour ink, water-blue low points, and a survey-
    // stake amber for the parcel line. Dark mode is "dusk in the field"
    // (deep green-gray, not graphite); light mode is "meadow at noon".
    terrain: {
      light: "#c3cdb0",
      dark: "#4c5546",
      note: "Vegetated ground albedo — pale sage in daylight, deep moss at dusk. Directional light still carries the landform."
    },
    "contour-minor": {
      light: "#7d886c",
      dark: "#8b957f",
      note: "Secondary contour ink in olive; density is attenuated in screen space."
    },
    "contour-index": {
      light: "#4d5740",
      dark: "#c2cab6",
      note: "Major contour ink, subordinate to the amber parcel boundary."
    },
    "sky-zenith": { light: "#e6eee8", dark: "#11151a" },
    "sky-horizon": { light: "#c9d3c2", dark: "#28312c" },
    "sky-ground": { light: "#a9b59c", dark: "#1a211c" },
    fog: { light: "#c9d3c2", dark: "#28312c" },
    "ao-tint": { light: "#56604c", dark: "#0e120f" },
    "terrain-parcel": { light: "#b5c58f", dark: "#66754f" },
    "terrain-edge": { light: "#8a9478", dark: "#262c26" },
    "light-sky": { light: "#f2f7ff", dark: "#dfe8f2" },
    "light-ground": { light: "#8f9c78", dark: "#5a6650" },
    "light-key": { light: "#fbf5e6", dark: "#fff2dc" },
    "light-neutral": { light: "#ffffff", dark: "#ffffff" },
    "road-edge": { light: "#9a9482", dark: "#3b3e36" },
    "structure-wall": { light: "#e9e2d3", dark: "#cbc4b6" },
    "structure-roof": { light: "#f7f3ea", dark: "#e6e1d7" },
    "structure-edge": { light: "#7b7462", dark: "#6e695e" },
    "parcel-corner": { light: "#7f5526", dark: "#e5ad4f" },
    "label-border": { light: "#c3c7b5", dark: "#4a5248" },
    accent: {
      light: "#9a6a35",
      dark: "#d49b45",
      note: "Survey-flag amber. Interaction accent (measure, previews, selection, rail state) — one step dimmer than the parcel line so the two stay distinguishable."
    },
    road: {
      light: "#d9d2c1",
      dark: "#8c897b",
      note: "Gravel/tan road surface; casing separates it from the terrain."
    },
    "parcel-line": {
      light: "#7f5526",
      dark: "#e5ad4f",
      note: "Deep umber on daylight terrain, warm amber at dusk — reads as a plat line / survey ribbon rather than a neon outline."
    },
    "parcel-fill": {
      light: "#a8bd7c",
      dark: "#7e8c55",
      note: "Grass-green tint. Unlit overlay: sits below the lit terrain's value in light mode, above it in dark, to read as a tint either way."
    },
    building: {
      light: "#cfc3a8",
      dark: "#7b786c",
      note: "Context massing remains below proposed structures in visual emphasis."
    },
    "building-roof": { light: "#b9ac92", dark: "#9e9a8d" },
    "label-bg": { light: "#f7f7f1", dark: "#1b201c" },
    "label-text": { light: "#3f4636", dark: "#eef0ea" },
    "marker-high": {
      light: "#b4503f",
      dark: "#de6a5c",
      note: "Brick red — distinct from the amber accents."
    },
    "marker-low": {
      light: "#5b86a4",
      dark: "#7fb2cd",
      note: "Water blue — semantic (low points, drainage), the one cool note in the palette."
    },
    // Sun-path arc. The dark side keeps the pale warm yellows that read
    // against the dusk scene; the light side deepens every stop into
    // amber/terracotta so the arc holds up on the daylight sky.
    "sun-arc-day": { light: "#c9871f", dark: "#f6c66a" },
    "sun-arc-night": {
      light: "#b1a48c",
      dark: "#707070",
      note: "Below-horizon segments. Neutral gray on dark (monochrome map); muted taupe on parchment."
    },
    "sun-rise": { light: "#c25e2e", dark: "#ffb077" },
    "sun-noon": { light: "#b3871a", dark: "#ffe16d" },
    "sun-set": { light: "#b34a26", dark: "#ff8a5a" },
    "sun-current": {
      light: "#c9871f",
      dark: "#fff1b0",
      note: "The slider-driven 'now' sphere while the sun is up."
    },
    "sun-below": {
      light: "#9b8c70",
      dark: "#6c5a36",
      note: "The 'now' sphere when parked below the horizon."
    }
  }
} satisfies Record<string, GroupSeed>;

type Seed = typeof SEED;
type Group = keyof Seed;

function toToken(group: string, name: string, seed: TokenSeed): ColorToken {
  return {
    path: `${group}/${name}`,
    group,
    name,
    cssVar: `--color-${group}-${name}`,
    light: seed.light,
    dark: seed.dark,
    note: seed.note
  };
}

/**
 * Structured, grouped token map. Typed so `colorTokens.text.default.light`
 * autocompletes. Use this for programmatic access (Three.js, canvas, etc).
 */
export const colorTokens = Object.fromEntries(
  Object.entries(SEED).map(([group, groupSeed]) => [
    group,
    Object.fromEntries(
      Object.entries(groupSeed).map(([name, seed]) => [
        name,
        toToken(group, name, seed)
      ])
    )
  ])
) as {
  [G in Group]: { [N in keyof Seed[G]]: ColorToken };
};

/** Flat list of every token, in Figma order. Handy for iteration (swatches, audits). */
export const COLOR_TOKENS: readonly ColorToken[] = Object.values(colorTokens).flatMap(
  (group) => Object.values(group) as ColorToken[]
);

/** Group display order, matching the Figma frame layout. */
export const COLOR_TOKEN_GROUPS: readonly Group[] = Object.keys(SEED) as Group[];

/** Tokens for a single group, in order. */
export function tokensForGroup(group: Group): readonly ColorToken[] {
  return Object.values(colorTokens[group]) as ColorToken[];
}

/** Resolve a token's value for the given mode. */
export function resolveColor(token: ColorToken, mode: ThemeMode): string {
  return mode === "dark" ? token.dark : token.light;
}

/**
 * Generate the CSS custom-property declarations. Inject this once at the
 * app root (e.g. as an inline `<style>` or imported sheet) when the color
 * migration begins. Light values live on `:root`; dark values apply when
 * `data-theme="dark"` is set on the root element (with a
 * `prefers-color-scheme` fallback for an undecided theme).
 *
 * Values stay as plain hex/rgba — `color(srgb …)` inside custom properties
 * can fail to resolve in `var()` on some engines, leaving backgrounds
 * transparent and letting the scrim bleed through as a washed-out gray.
 */
export function colorTokensCss(): string {
  const lightDecls = COLOR_TOKENS.map((t) => `  ${t.cssVar}: ${t.light};`).join("\n");
  const darkDecls = COLOR_TOKENS.map((t) => `  ${t.cssVar}: ${t.dark};`).join("\n");
  return [
    `:root {\n${lightDecls}\n}`,
    `[data-theme="dark"] {\n${darkDecls}\n}`,
    `@media (prefers-color-scheme: dark) {\n  :root:not([data-theme="light"]) {\n${darkDecls
      .split("\n")
      .map((line) => `  ${line}`)
      .join("\n")}\n  }\n}`
  ].join("\n\n");
}
